import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { allProducts } from "@/mocks/productDetail";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownhongyunmall.com";

const productsJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "商品列表 | 手机数码·美妆护肤·运动户外 | 皇冠鸿运商城",
    "description": "皇冠鸿运商城正品商品列表，涵盖手机数码、家用电器、美妆护肤、运动户外、奢侈品、摄影摄像等品类，工厂直营正品保障，极速配送，7天退换。",
    "url": `${SITE_URL}/products`,
    "inLanguage": "zh-CN",
    "isPartOf": {
      "@type": "WebSite",
      "name": "皇冠鸿运商城",
      "url": SITE_URL,
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "商品列表", "item": `${SITE_URL}/products` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "皇冠鸿运商城正品商品",
    "description": "手机数码、家用电器、美妆护肤、运动户外、奢侈品、摄影摄像，工厂直营正品保障",
    "url": `${SITE_URL}/products`,
    "numberOfItems": 21,
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Apple iPhone 15 Pro Max 256GB 钛金属", "url": `${SITE_URL}/product/1` },
      { "@type": "ListItem", "position": 2, "name": "华为 Mate 60 Pro 旗舰手机 512GB", "url": `${SITE_URL}/product/2` },
      { "@type": "ListItem", "position": 3, "name": "戴森 V15 Detect 无线吸尘器", "url": `${SITE_URL}/product/3` },
      { "@type": "ListItem", "position": 4, "name": "SK-II 神仙水精华液 230ml 礼盒装", "url": `${SITE_URL}/product/4` },
      { "@type": "ListItem", "position": 5, "name": "耐克 Air Jordan 1 Retro High OG", "url": `${SITE_URL}/product/5` },
      { "@type": "ListItem", "position": 6, "name": "小米 14 Ultra 徕卡影像旗舰", "url": `${SITE_URL}/product/6` },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "皇冠鸿运商城",
    "url": SITE_URL,
    "logo": "https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png",
    "description": "正品工厂直营电商平台，正品保障，极速配送，7天退换",
  },
];

const CATEGORIES = ["全部", "手机数码", "家用电器", "美妆护肤", "运动户外", "奢侈品", "摄影摄像"];
const SORT_OPTIONS = [
  { label: "综合排序", value: "default" },
  { label: "销量优先", value: "sales" },
  { label: "价格从低到高", value: "price_asc" },
  { label: "价格从高到低", value: "price_desc" },
  { label: "好评优先", value: "rating" },
];
const PAGE_SIZE = 8;

const tagColors: Record<string, string> = {
  爆款: "bg-red-500", 新品: "bg-green-500", 热销: "bg-orange-500",
  礼盒: "bg-pink-500", 限量: "bg-purple-500", 专业: "bg-blue-500", 奢品: "bg-amber-600",
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [search, setSearch] = useState("");
  const [likedIds, setLikedIds] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const filtered = useMemo(() => {
    let list = [...allProducts];
    if (category !== "全部") list = list.filter((p) => p.category === category);
    if (search.trim()) list = list.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || p.brand.toLowerCase().includes(search.toLowerCase()));
    if (priceMin) list = list.filter((p) => p.price >= Number(priceMin));
    if (priceMax) list = list.filter((p) => p.price <= Number(priceMax));
    if (sort === "sales") list.sort((a, b) => b.sales - a.sales);
    else if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [category, sort, priceMin, priceMax, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleLike = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setLikedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleCategoryChange = (cat: string) => { setCategory(cat); setPage(1); };
  const handleSortChange = (s: string) => { setSort(s); setPage(1); };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="正品商品列表 | 手机数码·美妆护肤·运动户外·奢侈品 | 皇冠鸿运商城"
        description="皇冠鸿运商城正品商品列表，精选手机数码、家用电器、美妆护肤、运动户外、奢侈品、摄影摄像，工厂直营正品保障，极速配送，7天无忧退换，全场满999减100。"
        keywords="正品商品,手机数码,美妆护肤,运动户外,奢侈品,工厂直营,正品保障,皇冠鸿运商城"
        canonical="/products"
        jsonLd={productsJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 正品保障 · 极速配送</span>
          <div className="flex items-center gap-4">
            <a href="/" className="hover:underline cursor-pointer flex items-center gap-1"><i className="ri-home-line"></i> 返回首页</a>
            <a href="#" className="hover:underline cursor-pointer">我的订单</a>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png" alt="Logo" className="h-9 w-auto object-contain" />
            <span className="text-red-600 font-bold text-lg hidden sm:block whitespace-nowrap" style={{ fontFamily: "'Noto Serif SC', serif" }}>皇冠鸿运商城</span>
          </a>
          <div className="flex-1 max-w-xl">
            <div className="flex items-center border-2 border-red-500 rounded-md overflow-hidden">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="搜索商品名称、品牌..."
                className="flex-1 px-4 py-2 text-sm outline-none"
              />
              <button className="bg-red-500 text-white px-5 py-2 text-sm font-medium whitespace-nowrap cursor-pointer">
                <i className="ri-search-line mr-1"></i>搜索
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <button onClick={() => navigate("/cart")} className="flex items-center gap-1 text-gray-600 hover:text-red-500 cursor-pointer text-sm whitespace-nowrap">
              <i className="ri-shopping-cart-line text-lg"></i>
              <span className="hidden md:inline">购物车</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-5">
          <a href="/" className="hover:text-red-500 cursor-pointer">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-600">商品列表</span>
          {category !== "全部" && (
            <>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-red-500">{category}</span>
            </>
          )}
        </div>

        <div className="flex gap-6">
          {/* Sidebar filters */}
          <aside className="w-52 flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-xl p-4 mb-4">
              <h3 className="font-bold text-gray-800 text-sm mb-3">商品分类</h3>
              <div className="space-y-1">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${category === cat ? "bg-red-50 text-red-500 font-medium" : "text-gray-600 hover:bg-gray-50"}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <h3 className="font-bold text-gray-800 text-sm mb-3">价格区间</h3>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={priceMin}
                  onChange={(e) => { setPriceMin(e.target.value); setPage(1); }}
                  placeholder="最低"
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-red-400"
                />
                <span className="text-gray-400 text-xs flex-shrink-0">—</span>
                <input
                  type="number"
                  value={priceMax}
                  onChange={(e) => { setPriceMax(e.target.value); setPage(1); }}
                  placeholder="最高"
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-red-400"
                />
              </div>
              <button
                onClick={() => { setPriceMin(""); setPriceMax(""); setPage(1); }}
                className="mt-2 w-full text-xs text-gray-400 hover:text-red-500 cursor-pointer text-center"
              >
                清除价格筛选
              </button>
            </div>
            <div className="bg-white rounded-xl p-4">
              <h3 className="font-bold text-gray-800 text-sm mb-3">品牌</h3>
              <div className="space-y-1">
                {["Apple", "华为", "小米", "戴森", "Nike", "SK-II", "索尼", "大疆"].map((brand) => (
                  <label key={brand} className="flex items-center gap-2 cursor-pointer py-1">
                    <input type="checkbox" className="accent-red-500" />
                    <span className="text-sm text-gray-600">{brand}</span>
                  </label>
                ))}
              </div>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Sort & view toggle bar */}
            <div className="bg-white rounded-xl px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSortChange(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${sort === opt.value ? "bg-red-500 text-white" : "text-gray-500 hover:text-red-500 hover:bg-red-50"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">共 {filtered.length} 件商品</span>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`w-8 h-8 flex items-center justify-center cursor-pointer transition-colors ${viewMode === "grid" ? "bg-red-500 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                  >
                    <i className="ri-grid-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`w-8 h-8 flex items-center justify-center cursor-pointer transition-colors ${viewMode === "list" ? "bg-red-500 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                  >
                    <i className="ri-list-check text-sm"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile category pills */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${category === cat ? "bg-red-500 text-white" : "bg-white text-gray-500 border border-gray-200"}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Product grid */}
            {paginated.length === 0 ? (
              <div className="bg-white rounded-xl py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-search-line text-3xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 font-medium">没有找到相关商品</p>
                <p className="text-gray-400 text-sm mt-1">试试调整筛选条件</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {paginated.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => navigate(`/product/${product.id}`)}
                    className="bg-white rounded-xl overflow-hidden cursor-pointer group hover:scale-[1.02] transition-transform duration-200"
                  >
                    <div className="relative overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-44 object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute top-2 left-2">
                        <span className={`${tagColors[product.tag] || "bg-gray-500"} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>{product.tag}</span>
                      </div>
                      <button onClick={(e) => toggleLike(product.id, e)} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/80 rounded-full cursor-pointer hover:bg-white transition-colors">
                        <i className={`text-sm ${likedIds.includes(product.id) ? "ri-heart-fill text-red-500" : "ri-heart-line text-gray-400"}`}></i>
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-gray-800 text-sm font-medium leading-snug line-clamp-2 mb-1">{product.name}</p>
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={`text-xs ${i < Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                        ))}
                        <span className="text-xs text-gray-400 ml-1">({product.reviewCount.toLocaleString()})</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-red-500 font-bold text-lg">¥{product.price.toLocaleString()}</span>
                          <span className="text-gray-400 text-xs line-through ml-1">¥{product.originalPrice.toLocaleString()}</span>
                        </div>
                      </div>
                      <p className="text-gray-400 text-xs mt-1">{product.sales.toLocaleString()}人购买</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {paginated.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => navigate(`/product/${product.id}`)}
                    className="bg-white rounded-xl overflow-hidden cursor-pointer hover:bg-red-50/30 transition-colors flex gap-4 p-4"
                  >
                    <div className="relative flex-shrink-0">
                      <img src={product.image} alt={product.name} className="w-28 h-28 object-cover object-top rounded-lg" />
                      <span className={`absolute top-1 left-1 ${tagColors[product.tag] || "bg-gray-500"} text-white text-xs px-1.5 py-0.5 rounded-full font-medium`}>{product.tag}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 font-medium text-sm mb-1 line-clamp-2">{product.name}</p>
                      <p className="text-gray-400 text-xs mb-2 line-clamp-1">{product.description}</p>
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={`text-xs ${i < Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                        ))}
                        <span className="text-xs text-gray-400 ml-1">({product.reviewCount.toLocaleString()})</span>
                        <span className="text-xs text-gray-300 mx-1">|</span>
                        <span className="text-xs text-gray-400">{product.sales.toLocaleString()}人购买</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-red-500 font-bold text-xl">¥{product.price.toLocaleString()}</span>
                          <span className="text-gray-400 text-xs line-through ml-2">¥{product.originalPrice.toLocaleString()}</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); }}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          加入购物车
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-red-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${page === i + 1 ? "bg-red-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-red-400 hover:text-red-500"}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-red-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
